<template>
  <div
    class="min-h-screen bg-gradient-to-br from-gray-50 to-white dark:from-gray-900 dark:to-gray-800"
  >
    <!-- Page Header -->
    <div class="bg-gradient-to-r from-indigo-600 to-purple-600 shadow-lg">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div class="flex items-center">
          <!-- Model Gateway Icon -->
          <div class="w-12 h-12 bg-white rounded-xl flex items-center justify-center mr-4">
            <svg
              class="w-7 h-7 text-indigo-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01"
              />
            </svg>
          </div>
          <div>
            <h1 class="text-2xl font-bold text-white">Model Gateway</h1>
            <div class="flex items-center space-x-3 text-indigo-100">
              <p>Centralized control plane for managing LLM access across your organization</p>
              <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-400/20 text-green-100 border border-green-400/30">
                <span class="w-1.5 h-1.5 rounded-full bg-green-400 mr-1.5 animate-pulse"></span>
                Gateway Online
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Tab Navigation -->
      <div class="mb-8">
        <div class="border-b border-gray-200 dark:border-gray-700">
          <nav class="-mb-px flex space-x-8" aria-label="Tabs">
            <!-- Base Models Tab -->
            <button
              @click="activeTab = 'base-models'"
              :class="[
                activeTab === 'base-models'
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300',
                'group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors',
              ]"
            >
              <svg
                :class="[
                  activeTab === 'base-models'
                    ? 'text-indigo-500 dark:text-indigo-400'
                    : 'text-gray-400 group-hover:text-gray-500 dark:group-hover:text-gray-300',
                  '-ml-0.5 mr-2 h-5 w-5',
                ]"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                />
              </svg>
              <span>Base Models</span>
            </button>

            <!-- Custom Models (BYOM) Tab -->
            <button
              @click="activeTab = 'custom-models'"
              :class="[
                activeTab === 'custom-models'
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300',
                'group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors',
              ]"
            >
              <svg
                :class="[
                  activeTab === 'custom-models'
                    ? 'text-indigo-500 dark:text-indigo-400'
                    : 'text-gray-400 group-hover:text-gray-500 dark:group-hover:text-gray-300',
                  '-ml-0.5 mr-2 h-5 w-5',
                ]"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                />
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
              <span>Custom Models (BYOM)</span>
            </button>

            <!-- Connections Tab -->
            <button
              @click="activeTab = 'connections'"
              :class="[
                activeTab === 'connections'
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300',
                'group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors',
              ]"
            >
              <svg
                :class="[
                  activeTab === 'connections'
                    ? 'text-indigo-500 dark:text-indigo-400'
                    : 'text-gray-400 group-hover:text-gray-500 dark:group-hover:text-gray-300',
                  '-ml-0.5 mr-2 h-5 w-5',
                ]"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                />
              </svg>
              <span>Connections</span>
            </button>

            <!-- ASAB Models Tab -->
            <button
              @click="activeTab = 'asab-models'"
              :class="[
                activeTab === 'asab-models'
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300',
                'group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors',
              ]"
            >
              <svg
                :class="[
                  activeTab === 'asab-models'
                    ? 'text-indigo-500 dark:text-indigo-400'
                    : 'text-gray-400 group-hover:text-gray-500 dark:group-hover:text-gray-300',
                  '-ml-0.5 mr-2 h-5 w-5',
                ]"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
              <span>ASAB Models</span>
            </button>
          </nav>
        </div>
      </div>

      <!-- Tab Content -->
      <div class="mt-6">
        <!-- Base Models Tab Content -->
        <div v-if="activeTab === 'base-models'" class="animate-fade-in">
          <BaseModelCatalog />
        </div>

        <!-- Custom Models (BYOM) Tab Content -->
        <div v-else-if="activeTab === 'custom-models'" class="animate-fade-in">
          <CustomModelList @create-connection="activeTab = 'connections'" />
        </div>

        <!-- Connections Tab Content -->
        <div v-else-if="activeTab === 'connections'" class="animate-fade-in">
          <CustomModelConnectionList @models-imported="handleModelsImported" />
        </div>

        <!-- ASAB Models Tab Content -->
        <div v-else-if="activeTab === 'asab-models'" class="animate-fade-in">
          <AsabModelList />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, onMounted, watch } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import BaseModelCatalog from '../components/BaseModelCatalog.vue';
  import CustomModelList from '../components/CustomModelList.vue';
  import CustomModelConnectionList from '../components/CustomModelConnectionList.vue';
  import AsabModelList from '../components/AsabModelList.vue';
  import { useCustomModels } from '../composables/useCustomModels';

  // Router
  const route = useRoute();
  const router = useRouter();

  // Tab state
  type TabType = 'base-models' | 'custom-models' | 'connections' | 'asab-models';
  const activeTab = ref<TabType>('base-models');

  const { fetchCustomModels } = useCustomModels();

  const handleModelsImported = async () => {
    // Refresh custom models list
    await fetchCustomModels();
    // Switch to custom models tab to show imported models
    activeTab.value = 'custom-models';
  };

  // Initialize tab from query parameter if present
  onMounted(() => {
    const tabParam = route.query.tab as string;
    if (tabParam && ['base-models', 'custom-models', 'connections', 'asab-models'].includes(tabParam)) {
      activeTab.value = tabParam as TabType;
    }
  });

  // Update URL query parameter when tab changes
  watch(activeTab, newTab => {
    router.replace({ query: { tab: newTab } });
  });
</script>

<style scoped>
  @keyframes fade-in {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .animate-fade-in {
    animation: fade-in 0.3s ease-out;
  }
</style>
